import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyGroup, leaveGroup } from '../../services/groupService';
import { redeemGiftCode } from '../../services/paymentService';

const RedeemGift = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasGroup, setHasGroup] = useState(true);

  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [leaving, setLeaving] = useState(false);

  const fetchGroup = async () => {
    setLoading(true);
    try {
      const data = await getMyGroup();
      setGroupData(data);
      setHasGroup(true);
    } catch (err) {
      setHasGroup(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroup();
  }, []);

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setRedeeming(true);
    setError('');
    setSuccess('');
    try {
      const data = await redeemGiftCode(code.trim());
      setSuccess(data.message);
      setCode('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to redeem code');
    } finally {
      setRedeeming(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm(`Leave "${groupData.group.name}" so you can create your own cookbook and redeem this gift yourself?`)) return;
    setLeaving(true);
    try {
      await leaveGroup();
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to leave cookbook');
      setLeaving(false);
    }
  };

  if (loading) return <p style={styles.status}>Loading...</p>;

  const isOwner = hasGroup && groupData.group.owner_id === user?.id;
  const isActive = hasGroup && groupData.subscription?.status === 'active';

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>🎁 Redeem a Gift Code</h2>

      {/* Case 1: no group at all yet */}
      {!hasGroup && (
        <>
          <p style={styles.text}>
            You'll need to create your own cookbook first before you can redeem a gift code — 
            it activates whichever cookbook you're the owner of.
          </p>
          <button onClick={() => navigate('/dashboard')} style={styles.button}>
            Go Create Your Cookbook
          </button>
        </>
      )}

      {/* Case 2: member of a group, but not the owner */}
      {hasGroup && !isOwner && (
        <>
          <p style={styles.text}>
            Only the owner of a cookbook can redeem a gift code — and you're currently a member of{' '}
            <strong>"{groupData.group.name}"</strong>, which someone else owns.
          </p>
          <p style={styles.text}>
            To use this gift yourself, you'd need to leave that cookbook and create your own.
            (Your gift code stays valid either way — nothing is used up until a redemption actually succeeds.)
          </p>
          <button onClick={handleLeave} style={styles.dangerButton} disabled={leaving}>
            {leaving ? 'Leaving...' : `Leave "${groupData.group.name}" to Use This Gift`}
          </button>
        </>
      )}

      {/* Case 3: owner, but already has an active plan */}
      {hasGroup && isOwner && isActive && (
        <p style={styles.text}>
          Your cookbook <strong>"{groupData.group.name}"</strong> already has an active plan, so
          there's nothing to redeem here. If this gift was meant for a different cookbook, share the
          code with that cookbook's owner instead.
        </p>
      )}

      {/* Case 4: owner, no active plan yet -- the actual redemption form */}
      {hasGroup && isOwner && !isActive && (
        <>
          <p style={styles.text}>
            Enter your gift code below to activate <strong>"{groupData.group.name}"</strong>.
          </p>
          <form onSubmit={handleRedeem} style={styles.form}>
            <input
              type="text"
              placeholder="COOK-XXXX-XXXX"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              style={styles.input}
              disabled={redeeming}
            />
            <button type="submit" style={styles.button} disabled={redeeming}>
              {redeeming ? 'Redeeming...' : 'Redeem'}
            </button>
          </form>
          {error && <p style={styles.error}>{error}</p>}
          {success && (
            <>
              <p style={styles.success}>{success}</p>
              <button onClick={() => navigate('/dashboard')} style={styles.button}>Go to Dashboard</button>
            </>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '480px', margin: '0 auto', backgroundColor: 'white', padding: '32px',
    borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center',
  },
  title: { color: '#e67e22', marginBottom: '16px' },
  text: { color: '#666', fontSize: '14px', lineHeight: 1.6, marginBottom: '16px' },
  form: { display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '10px' },
  input: {
    padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px',
    textAlign: 'center', letterSpacing: '1px', width: '200px',
  },
  button: {
    padding: '10px 20px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
  },
  dangerButton: {
    padding: '10px 20px', backgroundColor: 'white', color: '#e74c3c', border: '1px solid #e74c3c',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
  },
  error: { color: '#e74c3c', fontSize: '13px', marginTop: '8px' },
  success: { color: '#27ae60', fontSize: '14px', fontWeight: '600', marginTop: '8px', marginBottom: '16px' },
  status: { textAlign: 'center', color: '#999' },
};

export default RedeemGift;
